import { ApiErrorBody } from '@bartizo/shared';
import { z } from 'zod';

/**
 * The one place the dashboard talks to the API.
 *
 * Every response is validated against the shared schema before it is used. The
 * skill's rule is "validate at every boundary", and the browser is a boundary like
 * any other: a field the API renamed should fail here, loudly, rather than render
 * as `undefined` three components away.
 *
 * Requests are same-origin. The session travels in an `HttpOnly` cookie the browser
 * attaches by itself, which is also why no token is ever held in JavaScript — there
 * is nothing here for a script injected into this page to steal.
 */

/** Relative, so the dev proxy and the production install are the same code path. */
const BASE = '/v1';

/** Section 5's success envelope. `data` is checked by the caller's own schema. */
const ResultEnvelope = z.object({ data: z.unknown() });

/** Section 5's list envelope: the page, and where the next one starts. */
const PageEnvelope = z.object({ data: z.array(z.unknown()), next_cursor: z.string().nullable() });

/** A page of results, in the dashboard's own camelCase. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** A response that is not JSON at all: a proxy error page, or the network giving up. */
export class NetworkError extends Error {
  readonly code = 'network_error';
  constructor(message = 'Could not reach the Bartizo API') {
    super(message);
    this.name = 'NetworkError';
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/** True when the API is asking for the credential again (section 17's high-risk gate). */
export const needsReauth = (error: unknown): boolean =>
  isApiError(error) && error.code === 'reauth_required';

export const isUnauthenticated = (error: unknown): boolean =>
  isApiError(error) && error.code === 'unauthenticated';

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** The shape of `data`. Required, so no response reaches the UI unvalidated. */
  schema: z.ZodType<T>;
  signal?: AbortSignal;
}

type TransportOptions = Omit<RequestOptions<unknown>, 'schema'>;

/** Everything up to the envelope: the request, the failure shapes, the JSON. */
async function send(path: string, options: TransportOptions): Promise<unknown> {
  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // Explicit rather than relying on the default: the session depends on it.
      credentials: 'same-origin',
      headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError();
  }

  const payload: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const envelope = ApiErrorBody.safeParse(payload);
    if (envelope.success) {
      throw new ApiError(response.status, envelope.data.error.code, envelope.data.error.message);
    }
    // A failure the API did not shape: a proxy, a gateway, or a crash before the
    // error plugin ran. Saying so beats inventing a code that means nothing.
    throw new ApiError(response.status, 'unexpected_error', 'The API returned an unexpected error');
  }

  return payload;
}

const malformed = (): ApiError =>
  new ApiError(200, 'malformed_response', 'The API returned an unexpected body');

export async function apiRequest<T>(path: string, options: RequestOptions<T>): Promise<T> {
  const payload = await send(path, options);

  const parsed = ResultEnvelope.safeParse(payload);
  if (!parsed.success) throw malformed();

  const data = options.schema.safeParse(parsed.data.data);
  // Loud on purpose: a contract that has drifted is a bug to fix, not a value to
  // paper over with a default.
  if (!data.success) throw malformed();
  return data.data;
}

/**
 * The same, for a route that pages (section 5: `{ data, next_cursor }`).
 *
 * A separate function rather than a flag on `apiRequest`, because the return type
 * differs and a discriminated option would leave every caller reading as if it
 * might be either.
 *
 * Every item is validated, so one malformed row is a failure rather than something
 * that renders as blank halfway down a table.
 */
export async function apiPageRequest<T>(
  path: string,
  options: RequestOptions<T>,
): Promise<Page<T>> {
  const payload = await send(path, options);

  const parsed = PageEnvelope.safeParse(payload);
  if (!parsed.success) throw malformed();

  const items = z.array(options.schema).safeParse(parsed.data.data);
  if (!items.success) throw malformed();

  return { items: items.data, nextCursor: parsed.data.next_cursor };
}
